// src/utils/selectorsProduct.js
//
// Transforms raw datasets -> Product view sections (tiles shape your UI expects).
// Defensive/MVP: tolerates missing fields & unknown shapes.

import { slugify, jtbdKeyFromName, normProductName, toNumber, clamp } from './normalize';
import {
  describeJtbdImportance, describeJtbdSatisfaction, describeReasonNotChosenImportance,
  describeInsightSentimentIntensity
} from './qualitative';
import { computeWinScore, computeGapScore } from './scoring';

/* ------------------------------- helpers ------------------------------- */

const take = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n) : []);
const sum = (a, b) => a + b;
const avg = (nums) => (nums.length ? nums.reduce(sum, 0) / nums.length : 0);

const capFirst = (s) => (typeof s === 'string' && s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// unified accessor w/ fallbacks
const get = (obj, path, dflt = undefined) => {
  try {
    const parts = Array.isArray(path) ? path : String(path).split('.');
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return dflt;
      cur = cur[p];
    }
    return cur == null ? dflt : cur;
  } catch {
    return dflt;
  }
};

// Normalize for fuzzy compare
function norm(s) {
  return (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '');
}

// Some stories use different keys; normalize here
function readImportance(job) {
  const v =
    job?.importance_score ??
    job?.importance ??
    job?.job_importance ??
    job?.importance_of_job ??
    job?.importanceLevel;

  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function readSatisfaction(job) {
  const v =
    job?.satisfaction ??
    job?.satisfaction_score ??
    job?.satisfaction_level ??
    job?.satisfactionLevel;

  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Extract a list of receipts (quote + optional timestamp/url).
 * Always returns { quote, url, timestamp } and dedups by quote text.
 */
function extractReceipts(node, limit = 2) {
    if (!node) return [];
    const push = (arr, quote, url, ts) => {
      if (!quote) return;
      arr.push({ quote: String(quote), url: url || '', timestamp: ts || '' });
    };
  
    const candidates = [];
  
    // 1) Generic containers: receipts / quotes / representative_quotes / evidence
    //    Support both arrays and objects (e.g., {receipt1: {...}, receipt2: {...}})
    const collect = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val.filter(Boolean);
      if (typeof val === 'object') return Object.values(val || {}).filter(Boolean);
      return [];
    };
  
    const generics = []
      .concat(collect(get(node, 'receipts')))
      .concat(collect(get(node, 'quotes')))
      .concat(collect(get(node, 'representative_quotes')))
      .concat(collect(get(node, 'evidence')));
  
    for (const r of generics) {
      if (typeof r === 'string') {
        push(candidates, r, '', r.timestamp || r.ts || '');
      } else if (r && typeof r === 'object') {
        push(
          candidates,
          r.quote || r.text || r.snippet || r.value,
          r.url || r.link || '',
          r.timestamp || r.ts || (Array.isArray(r.timestamps) ? r.timestamps[0] : r.timestamps)
        );
      }
    }
  
    // 2) Schema-specific pairs: scan all *_quote keys and look for *_timestamp or *_timestamps
    if (node && typeof node === 'object') {
      for (const k of Object.keys(node)) {
        if (/_quote$/.test(k) && node[k]) {
          const base = k.replace(/_quote$/, '');
          const ts = node[`${base}_timestamp`] || node[`${base}_timestamps`];
          push(candidates, node[k], '', Array.isArray(ts) ? ts[0] : ts);
        }
      }
    }
  
    // 3) Minimal fallback: direct {quote, timestamp} on the node itself
    if (node && typeof node === 'object' && (node.quote || node.timestamp)) {
      push(candidates, node.quote, '', node.timestamp);
    }
  
    // Dedup + cap
    const out = [];
    const seen = new Set();
    for (const rec of candidates) {
      const key = (rec.quote || '').trim().toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        out.push(rec);
      }
      if (out.length >= limit) break;
    }
    return out;
  }
  

/* ----------------------- core aggregations (MVP) ----------------------- */

/**
 * Product filtering: tolerant matches across common fields & nested shapes.
 */
function matchStoryToProduct(story, selectedProductRaw) {
  const sel = norm(selectedProductRaw);

  const candidates = [
    story.solution_choice_category,
    story.solution_choice,
    story.chosen_solution_category,
    story.chosen_solution_name,
    story.product_name,
    story.segmentation_data?.product_name,
  ].filter(Boolean);

  const extra = [];
  if (Array.isArray(story.products_used)) {
    for (const p of story.products_used) extra.push(p?.name || p?.title || p?.product_name);
  }

  const all = [...candidates, ...extra].filter(Boolean).map(norm);
  return all.some((v) => v === sel || v.includes(sel) || sel.includes(v));
}

function selectStoriesForProduct(customerStories, selectedProductRaw) {
  return (customerStories || []).filter((s) => matchStoryToProduct(s, selectedProductRaw));
}

/**
 * Group by JTBD category; collect arrays for importance/satisfaction.
 */
function groupByJob(stories) {
  const map = new Map();
  for (const s of stories) {
    const jobs = get(s, 'jobs_to_be_done', []);
    for (const j of jobs) {
      const name = j.job_to_be_done_category || j.raw_job_to_be_done || '';
      if (!name) continue;
      const key = jtbdKeyFromName(name);
      const bucket =
        map.get(key) ||
        {
          key,
          title: name,
          importance: [],
          satisfaction: [],
          receipts: [],
          stories: [],
        };

      // ✅ use tolerant readers
      const imp = readImportance(j);
      const sat = readSatisfaction(j);
      if (imp !== undefined) bucket.importance.push(imp);
      if (sat !== undefined) bucket.satisfaction.push(sat);

      // receipts: job-level else story-level
      const recs = extractReceipts(j, 2);
      bucket.receipts = bucket.receipts.concat(recs.length ? recs : extractReceipts(s, 2));

      bucket.stories.push(s);
      map.set(key, bucket);
    }
  }
  return Array.from(map.values());
}

// Random sampling utility
function randomSample(arr, n) {
    const a = Array.isArray(arr) ? [...arr] : [];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a.slice(0, n);
  }
  
  // Collect satisfaction-based receipts for a given JTBD title
  function collectJobSatisfactionReceipts(stories, jtbdTitle, variant /* 'win'|'gap' */, limit = 2) {
    const rows = [];
    for (const s of stories) {
      const jobs = get(s, 'jobs_to_be_done', []) || [];
      for (const j of jobs) {
        const name = j.job_to_be_done_category || j.raw_job_to_be_done || j.job_to_be_done || '';
        if (!name || norm(name) !== norm(jtbdTitle)) continue;
  
        const sat = readSatisfaction(j);
        const quote = j.satisfaction_quote || j.satisfaction_quotes || j.quote || '';
        const ts = j.satisfaction_timestamps || j.satisfaction_timestamp || j.timestamp || '';
        const timestamp = Array.isArray(ts) ? ts[0] : ts;
  
        if (quote) {
          rows.push({
            satisfaction: Number.isFinite(sat) ? sat : -1,
            receipt: { quote: String(quote), url: '', timestamp }
          });
        }
      }
    }
  
    if (!rows.length) return [];
  
    // Sort by satisfaction desc/asc depending on variant
    rows.sort((a, b) => a.satisfaction - b.satisfaction);
    const ordered = variant === 'win' ? rows.slice().reverse() : rows; // wins = high sat, gaps = low sat
  
    const receipts = ordered.map(r => r.receipt);
    return randomSample(receipts, limit);
  }
  

/**
 * Wins & Gaps (cap 4 each)
 */
function buildWinsAndGapsRows(byJob) {
    const enriched = byJob.map((j) => {
      const count = j.stories.length;
      const avgImportance = avg(j.importance);
      const avgSatisfaction = avg(j.satisfaction);
  
      const winScore = computeWinScore({
        importance: avgImportance,
        satisfaction: avgSatisfaction,
        interviews: count,
      });
  
      const gapScore = computeGapScore({
        importance: avgImportance,
        satisfaction: avgSatisfaction,
      });
  
      // New classification: whichever score “wins” determines the tile side.
      const isWin = winScore >= gapScore;
  
      return {
        ...j,
        count,
        avgImportance,
        avgSatisfaction,
        winScore,
        gapScore,
        isWin,
      };
    });
  
    const wins = enriched
      .filter((x) => x.isWin)
      .sort((a, b) => b.winScore - a.winScore)
      .slice(0, 4)
      .map((x) => ({
        type: 'job',
        variant: 'win',
        title: x.title,
        count: x.count,
        avgImportance: Number(x.avgImportance.toFixed(1)),
        avgSatisfaction: Number(x.avgSatisfaction.toFixed(1)),
        receipts: collectJobSatisfactionReceipts(x.stories, x.title, 'win', 2),
      }));
  
    const gaps = enriched
      .filter((x) => !x.isWin)
      .sort((a, b) => b.gapScore - a.gapScore)
      .slice(0, 4)
      .map((x) => ({
        type: 'job',
        variant: 'gap',
        title: x.title,
        count: x.count,
        avgImportance: Number(x.avgImportance.toFixed(1)),
        avgSatisfaction: Number(x.avgSatisfaction.toFixed(1)),
        receipts: collectJobSatisfactionReceipts(x.stories, x.title, 'gap', 2),
      }));
  
    return { wins, gaps };
  }
  

/**
 * Triggers (cap 4)
 */
function buildTriggers(stories) {
  const buckets = new Map();

  for (const s of stories) {
    const title = s.trigger_category || s.trigger || '';
    if (!title) continue;
    const key = title.toLowerCase();
    const b = buckets.get(key) || { title, count: 0, receipts: [] };
    b.count += 1;
    // Receipt = the trigger text itself (no video/timestamps)
    const rec = { quote: (s.trigger || title).toString(), url: '' };
    if (!b.receipts.find((r) => r.quote === rec.quote)) b.receipts.push(rec);
    buckets.set(key, b);
  }

  return Array.from(buckets.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
    .map((t) => ({ type: 'trigger', title: t.title, count: t.count, receipts: take(t.receipts, 2) }));
}

/**
 * Previous solutions (cap 4). Show top 2 reasons with counts, percents, receipts.
 */
function buildPreviousSolutions(stories) {
  const byPrev = new Map();

  for (const s of stories) {
    const title = s.previous_solution_category || s.previous_solution || '';
    if (!title) continue;
    const key = title.toLowerCase();
    const bucket = byPrev.get(key) || { title, count: 0, reasons: [] };
    bucket.count += 1;

    const reasons = get(s, 'reasons_not_good_enough', []);
    for (const r of reasons) {
      const reason = r.reason_not_good_enough_category || r.reason_not_good_enough || r.reason || r.title || '';
      if (!reason) continue;

      const existing = bucket.reasons.find((rr) => (rr.reason || '').toLowerCase() === reason.toLowerCase());
      const receipts = extractReceipts(r, 2).length ? extractReceipts(r, 2) : extractReceipts(s, 2);

      if (existing) {
        existing.count = (existing.count || 0) + 1;
        existing.receipts = existing.receipts.concat(receipts).slice(0, 2);
      } else {
        bucket.reasons.push({ reason, count: 1, receipts });
      }
    }

    byPrev.set(key, bucket);
  }

  const tiles = [];
  for (const b of byPrev.values()) {
    b.reasons.forEach((r) => {
      r.percent = b.count ? Math.round((r.count / b.count) * 100) : undefined;
    });
    b.reasons.sort((a, b) => b.count - a.count);
    tiles.push({
      type: 'solution',
      subtype: 'previous',
      title: b.title,
      count: b.count,
      reasons: b.reasons.slice(0, 2),
    });
  }

  return tiles.sort((a, b) => b.count - a.count).slice(0, 4);
}

/**
 * Considered solutions (cap 4). Reasons with qualitative importance + receipts.
 */
function buildConsideredSolutions(stories) {
  // Map: solutionKey -> { title, count (stories mentioning solution), reasons: Map<reasonKey, {reason, count, receipts:[]}> }
  const bySolution = new Map();

  for (const s of stories) {
    const considered = get(s, 'considered_solutions', []) || [];
    for (const sol of considered) {
      const title = sol.considered_solution_category || sol.considered_solution || sol.name || 'Considered solution';
      const solKey = (title || '').toLowerCase();

      let solBucket = bySolution.get(solKey);
      if (!solBucket) {
        solBucket = { title, count: 0, reasons: new Map() };
        bySolution.set(solKey, solBucket);
      }
      solBucket.count += 1; // this story mentioned the solution

      const reasons = get(sol, 'reasons_not_chosen', []) || [];
      for (const r of reasons) {
        const reasonTitle = r.reason_not_chosen_category || r.reason_not_chosen || r.reason || r.title || '';
        if (!reasonTitle) continue;
        const reasonKey = reasonTitle.toLowerCase();

        let rb = solBucket.reasons.get(reasonKey);
        if (!rb) {
          rb = { reason: reasonTitle, count: 0, receipts: [] };
          solBucket.reasons.set(reasonKey, rb);
        }
        rb.count += 1;

        // receipts for this reason (max 2 per reason)
        const rRecs = extractReceipts(r, 2);
        const sRecs = rRecs.length ? rRecs : extractReceipts(s, 2);
        // Keep cap 2 but prefer reason-level receipts first
        const merged = [...rb.receipts, ...sRecs].slice(0, 2);
        rb.receipts = merged;

        // optional: carry the qualitative importance (if present)
        const importance = toNumber(r.reason_not_chosen_importance ?? r.importance);
        if (Number.isFinite(importance)) {
          rb.importance = importance;
          rb.importanceLabel = describeReasonNotChosenImportance(importance);
        }
      }
    }
  }

  // Emit tiles (cap 4 tiles)
  return Array.from(bySolution.values())
    .map((b) => {
      // Convert reasons map to array and compute percent per reason
      const reasonsArr = Array.from(b.reasons.values())
        .map((rr) => ({
          reason: rr.reason,
          count: rr.count,
          percent: b.count ? Math.round((rr.count / b.count) * 100) : 0,
          importance: rr.importance,
          importanceLabel: rr.importanceLabel,
          receipts: rr.receipts.slice(0, 2), // ensure cap
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 2); // only top 2 reasons per tile

      return {
        type: 'solution',
        subtype: 'considered',
        title: b.title,
        count: b.count,
        reasons: reasonsArr,
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 4);
}


/**
 * Impressions (positives/negatives) (cap 4 each).
 * Derived from positives_of_solution_choice[] / drawbacks_of_solution_choice[].
 */
function buildImpressions(stories) {
  const pos = new Map();
  const neg = new Map();

  const push = (bucket, title, importance, receipts) => {
    const key = (title || '').toLowerCase();
    if (!key) return;
    const b = bucket.get(key) || { title, count: 0, importanceVals: [], receipts: [] };
    b.count += 1;
    if (Number.isFinite(importance)) b.importanceVals.push(importance);
    if (receipts?.length) b.receipts = b.receipts.concat(receipts);
    bucket.set(key, b);
  };

  for (const s of stories) {
    const positives = get(s, 'positives_of_solution_choice', []);
    for (const it of positives) {
      const title = it.positive_category || it.positive || it.title || '';
      if (!title) continue;
      const importance = toNumber(it.positive_importance ?? it.importance);
      const receipts = extractReceipts(it, 2).length ? extractReceipts(it, 2) : extractReceipts(s, 2);
      push(pos, title, importance, receipts);
    }

    const drawbacks = get(s, 'drawbacks_of_solution_choice', []);
    for (const it of drawbacks) {
      const title = it.drawback_category || it.drawback || it.title || '';
      if (!title) continue;
      const importance = toNumber(it.drawback_importance ?? it.importance);
      const receipts = extractReceipts(it, 2).length ? extractReceipts(it, 2) : extractReceipts(s, 2);
      push(neg, title, importance, receipts);
    }
  }

  const toTiles = (bucket, variant) =>
    Array.from(bucket.values())
      .map((b) => {
        const importance = avg(b.importanceVals);
        const score = Math.round(clamp(importance, 0, 10) * b.count);
        return {
          type: 'impression',
          variant,
          title: capFirst(b.title),          // <-- ensure first letter is capitalized
          count: b.count,
          importance: Number((importance || 0).toFixed(1)),
          score,
          receipts: take(b.receipts, 2),
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);

  return {
    positives: toTiles(pos, 'positive'),
    negatives: toTiles(neg, 'negative'),
  };
}



function findJobMapForTitle(jobMaps, jtbdTitle) {
    if (!jobMaps) return undefined;
    const want = norm(jtbdTitle);
  
    // 1) Try direct key hits (slug/normalized keys)
    const keyCandidates = Object.keys(jobMaps || {});
    for (const k of keyCandidates) {
      const nk = norm(k);
      if (nk === want || nk.includes(want) || want.includes(nk)) {
        return jobMaps[k];
      }
    }
  
    // 2) Scan each map’s metadata/title fields
    for (const k of keyCandidates) {
      const m = jobMaps[k];
      const titleFields = [
        m?.title,
        m?.job_title,
        m?.job_to_be_done,
        m?.job_to_be_done_category,
        m?.name,
      ].filter(Boolean);
  
      for (const tf of titleFields) {
        const nt = norm(tf);
        if (nt === want || nt.includes(want) || want.includes(nt)) {
          return m;
        }
      }
    }
  
    return undefined;
  }  

/**
 * Signature UX patterns (3 rows; each row = a top Job for this Product).
 * Each row shows up to 4 workflow steps for the selected product; fallback to general steps.
 */
function buildSignatureUx(storiesForProduct, jobMaps, selectedProductNameRaw) {
  const selectedProductName = normProductName(selectedProductNameRaw).toLowerCase();

  // Top jobs by interview count (for selector options)
  const jobsGrouped = groupByJob(storiesForProduct)
    .sort((a, b) => b.stories.length - a.stories.length);

  // Build a fast lookup of jobMap by normalized title
  const stageOrder = ['define','locate','prepare','confirm','execute','monitor','modify','conclude'];
  const stageRank = (s) => {
    const k = (s || '').toString().trim().toLowerCase();
    const i = stageOrder.indexOf(k);
    return i >= 0 ? i : 999;
  };

  const normalizeStages = (mapObj) => {
    if (!mapObj) return [];
    const raw = mapObj.stages;
    if (Array.isArray(raw)) {
      return raw.map((s) => ({ stage: s.stage || s.name || s.title || '', steps: Array.isArray(s.steps) ? s.steps : [] }));
    }
    if (raw && typeof raw === 'object') {
      const arr = [];
      for (const [stageKey, stepsVal] of Object.entries(raw)) {
        const steps = Array.isArray(stepsVal) ? stepsVal : (stepsVal?.steps || []);
        arr.push({ stage: stageKey, steps });
      }
      return arr;
    }
    return [];
  };

  // Build rows for *all* top jobs (for selector UI), but the renderer will only show one row
  const rows = [];
  for (let i = 0; i < Math.min(3, jobsGrouped.length); i++) {
    const j = jobsGrouped[i];
    const rowLabel = j.title;
    const rowInterviewCount = j.stories.length;

    const map = findJobMapForTitle(jobMaps, j.title);
    const normStages = normalizeStages(map);

    const matched = [];
    if (normStages.length) {
      for (const stage of normStages) {
        const stageName = stage.stage || '';
        for (const step of (stage.steps || [])) {
          const pwfList = Array.isArray(step.product_workflow_steps) ? step.product_workflow_steps : [];
          const forThisProduct = pwfList.filter((pwf) => {
            const pn = normProductName(pwf.product_name || pwf.product || '').toLowerCase();
            return pn && pn.includes(selectedProductName);
          });

          if (forThisProduct.length) {
            // pick best satisfaction
            const picked = [...forThisProduct].sort((a, b) => toNumber(b.satisfaction_score) - toNumber(a.satisfaction_score))[0];
            matched.push({
              stage: stageName,
              jtbdStepName: step.step_name || step.name || step.title || '',
              title: picked.name || picked.step_name || step.name || '',
              description: picked.description || step.description || '',
              satisfaction: Number((toNumber(picked.satisfaction_score ?? step.satisfaction_score)).toFixed(1)),
              satisfactionJustification: picked.satisfaction_score_justification || step.satisfaction_score_justification || '',
              receipts: extractReceipts(picked, 2)
            });
          }
        }
      }
    }

    // Fallback to general steps if none matched
    if (!matched.length && normStages.length) {
      for (const stage of normStages) {
        const stageName = stage.stage || '';
        for (const step of (stage.steps || [])) {
          matched.push({
            stage: stageName,
            jtbdStepName: step.step_name || step.name || step.title || '',
            title: step.name || step.title || '',
            description: step.description || '',
            satisfaction: Number((toNumber(step.satisfaction_score)).toFixed(1)),
            satisfactionJustification: step.satisfaction_score_justification || '',
            receipts: extractReceipts(step, 2)
          });
        }
      }
    }

    // Order by stage sequence first, then by (higher) satisfaction
    const ordered = matched
      .sort((a, b) => {
        const sr = stageRank(a.stage) - stageRank(b.stage);
        return sr !== 0 ? sr : (b.satisfaction - a.satisfaction);
      })
      //.slice(0, 4); // don't include this; show all steps

    rows.push({
      rowLabel,                  // job title only (TilesShowcase will add counts/percent in selector text)
      rowInterviewCount,
      tiles: ordered.map(s => ({
        type: 'workflowStep',
        jtbdStepName: s.jtbdStepName,
        title: s.title,
        description: s.description,
        satisfaction: s.satisfaction,
        satisfactionJustification: s.satisfactionJustification,
        receipts: s.receipts
      }))
    });
  }

  return rows;
}

  

/**
 * Insights (positive/negative) scoped to the selected Product.
 * Pull ONLY from: story.jobs_to_be_done[].insights[]
 * …but only for those job objects where job_to_be_done (or *_category) === selected product.
 */
function buildInsightsForProduct(stories, selectedProductNameRaw) {
  const pos = new Map();
  const neg = new Map();

  const norm = (s) =>
    (s || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s]/g, '');

  const strictProductEq = (story, selected) =>
    norm(story?.solution_choice_category) === norm(selected);

  const asNumber = (v) => {
    const n = toNumber(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Push insight row into a bucket keyed by insight title/category
  const push = (bucket, { title, intensity, quote, timestamp }) => {
    if (!title) return;
    const key = title.toLowerCase();
    const b = bucket.get(key) || { title, rows: [] };
    b.rows.push({ intensity: asNumber(intensity), quote, timestamp });
    bucket.set(key, b);
  };

  for (const s of stories) {
    if (!strictProductEq(s, selectedProductNameRaw)) continue;

    const jobs = get(s, 'jobs_to_be_done', []) || [];
    for (const j of jobs) {
      const jInsights = get(j, 'insights', []) || [];
      for (const it of jInsights) {
        const overall = (it.insight_sentiment_overall || it.type || it.variant || it.insight_type || '').toLowerCase();
        const isPositive = overall === 'positive';
        const isNegative = overall === 'negative' || overall === 'wish list' || overall === 'wishlist' || overall === 'wish';

        // Title/category for grouping tiles
        const title =
          it.insight_category ||
          it.title ||
          it.theme ||
          it.topic ||
          it.category ||
          it.insight ||
          '';

        if (!title) continue;

        // Intensity used for ranking within the bucket
        const intensity = it.insight_sentiment_score ?? it.sentiment ?? it.intensity;

        // Receipt: take insight_quote + timestamp (or first timestamps)
        const q = it.insight_quote || it.quote || '';
        const tsRaw = it.insight_timestamp ?? it.insight_timestamps ?? it.timestamp ?? it.timestamps;
        const ts = Array.isArray(tsRaw) ? tsRaw[0] : tsRaw || '';

        // Only push rows that actually have a quote (so we show a clickable receipt)
        if (!q) continue;

        const row = { title, intensity, quote: String(q), timestamp: ts };

        if (isPositive) push(pos, row);
        if (isNegative) push(neg, row);
      }
    }
  }

  // From each bucket, pick a random sample of up to 2 receipts from the highest-intensity rows
  const toTiles = (bucket, variant) =>
    Array.from(bucket.values())
      .map((b) => {
        // Sort rows by intensity desc, take a head window, then random sample 2
        const sorted = [...b.rows].sort((a, c) => c.intensity - a.intensity);
        const window = sorted.slice(0, Math.max(2, Math.ceil(sorted.length * 0.25))); // top 25% (at least 2)
        const shuffled = window.sort(() => Math.random() - 0.5);
        const receipts = shuffled.slice(0, 2).map((r) => ({
          quote: r.quote,
          url: '',                 // we rely on timestamp-based linking in ReceiptsList
          timestamp: r.timestamp || ''
        }));

        // Score = avg intensity * number of mentions (rows)
        const avgIntensity = b.rows.length
          ? b.rows.reduce((sum, r) => sum + asNumber(r.intensity), 0) / b.rows.length
          : 0;
        const score = Math.round(clamp(avgIntensity, 0, 10) * b.rows.length);

        return {
          type: 'insight',
          variant,
          title: capFirst(b.title),
          count: b.rows.length,
          avgSentiment: Number(avgIntensity.toFixed(1)),
          score,
          receipts
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);

  return {
    positives: toTiles(pos, 'positive'),
    negatives: toTiles(neg, 'negative')
  };
}

  

/**
 * Situations (continue / no_longer) (cap 4 each).
 * Uses your actual fields:
 *  - current_solution_still_used_in_these_situations[]
 *  - not_used_in_these_situations[]
 */
function buildSituations(stories) {
  const cont = new Map();
  const stop = new Map();

  const feed = (bucket, rec) => {
    const key = (rec.title || '').toLowerCase();
    if (!key) return;
    const b = bucket.get(key) || { title: rec.title, rows: [] };
    b.rows.push(rec);
    bucket.set(key, b);
  };

  for (const s of stories) {
    const still = get(s, 'current_solution_still_used_in_these_situations', []) || [];
    for (const it of still) {
      const title = it.situation_category || it.title || it.situation || it.context || '';
      const quote = it.quote || '';
      const ts = Array.isArray(it.timestamp) ? it.timestamp[0] : it.timestamp || '';
      if (!title || !quote) continue;
      feed(cont, { title, quote: String(quote), timestamp: ts });
    }

    const notUsed = get(s, 'not_used_in_these_situations', []) || [];
    for (const it of notUsed) {
      const title = it.situation_category || it.title || it.situation || it.context || '';
      const quote = it.quote || '';
      const ts = Array.isArray(it.timestamp) ? it.timestamp[0] : it.timestamp || '';
      if (!title || !quote) continue;
      feed(stop, { title, quote: String(quote), timestamp: ts });
    }
  }

  const toTiles = (bucket, variant) =>
    Array.from(bucket.values())
      .sort((a, b) => b.rows.length - a.rows.length)
      .slice(0, 4)
      .map((b) => ({
        type: 'situation',
        variant,
        title: b.title,
        count: b.rows.length,
        receipts: b.rows.slice(0, 2).map((r) => ({
          quote: r.quote,
          url: '',                 // timestamp-only; link built in ReceiptsList
          timestamp: r.timestamp || ''
        }))
      }));

  return {
    continue: toTiles(cont, 'continue'),
    no_longer: toTiles(stop, 'no_longer')
  };
}

/* -------------------------- public main selector -------------------------- */

export function getProductSections({ productKey, customerStories = [], jobMaps = {} }) {
    // 1) select stories for this product (fuzzy match)
    const storiesForProduct = selectStoriesForProduct(customerStories, productKey);
    const sectionTotal = storiesForProduct.length;
  
    // (optional) safe debug after the var exists
    console.debug('[getProductSections]', {
      productKey,
      storiesForProduct: sectionTotal
    });
  
    // 2) Wins vs Gaps
    const byJob = groupByJob(storiesForProduct);
    const { wins, gaps } = buildWinsAndGapsRows(byJob);
  
    // 3) Triggers
    const triggers = buildTriggers(storiesForProduct);
  
    // 4) Previous solutions
    const previous = buildPreviousSolutions(storiesForProduct);
  
    // 5) Considered solutions
    const considered = buildConsideredSolutions(storiesForProduct);
  
    // 6) Impressions
    const impressions = buildImpressions(storiesForProduct);
  
    // 7) Signature UX patterns (pass productKey!)
    const sigRows = buildSignatureUx(storiesForProduct, jobMaps, productKey);
  
    // 8) Insights
    const insights = buildInsightsForProduct(storiesForProduct, productKey);
  
    // 9) Situations
    const situations = buildSituations(storiesForProduct);
  
    // Assemble sections (caps already handled in builders)
    const sections = [
      {
        key: 'product.winVsStruggle',
        metaTitle: 'Jobs To Be Done',
        title: 'Where does this solution win vs struggle?', // rename freely
        section_total_interviews: sectionTotal,
        rows: [{ tiles: wins }, { tiles: gaps }],
      },
      {
        key: 'product.triggers',
        metaTitle: 'Buying Behaviors',
        title: 'Why do customers switch to this solution? (Triggers)', // rename freely
        section_total_interviews: sectionTotal,
        tiles: triggers,
      },
      {
        key: 'product.previousSolutions',
        title: 'What do they switch away from? (Previous solutions)', // rename freely
        section_total_interviews: sectionTotal,
        tiles: previous,
      },
      {
        key: 'product.consideredSolutions',
        title: 'What else do they consider? (Considered solutions)', // rename freely
        section_total_interviews: sectionTotal,
        tiles: considered,
      },
      {
        key: 'product.impressions',
        metaTitle: 'Product Usage Insights',
        title: 'What are their overall impressions of this Product?', // rename freely
        section_total_interviews: sectionTotal,
        rows: [{ tiles: impressions.positives }, { tiles: impressions.negatives }],
      },
      {
        key: 'product.signatureUx',
        title: 'Signature UX patterns (Product workflow steps)', // rename freely
        updatedAt: 'Last updated: about 1 month ago',
        section_total_interviews: sectionTotal,
        rows: sigRows,
      },
      {
        key: 'product.likesDislikes',
        title: 'What are their likes and dislikes in using this Product?', // rename freely
        section_total_interviews: sectionTotal,
        rows: [{ tiles: insights.positives }, { tiles: insights.negatives }],
      },
      {
        key: 'product.situations',
        title: 'Situations where customers continue/stop using the Product', // rename freely
        section_total_interviews: sectionTotal,
        rows: [{ tiles: situations.continue }, { tiles: situations.no_longer }],
      },
    ];  
  
    return { sections };
  }
  
